'use client';

import { CaretLeft, CaretRight } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface PaginationNavProps {
  /** Current page, 1-based. */
  page: number;
  /** Total number of pages. */
  totalPages: number;
  /** Called with the target 1-based page when the user navigates. */
  onPageChange: (page: number) => void;
  className?: string;
}

/**
 * Numbered pagination control matching Figma "Navigation" (node 465:4314):
 * a prev caret, smart-ellipsis page cells, and a next caret. The active page
 * uses the brand tint (`bg-ds-brand-subtlest` / `text-ds-brand-subtle`);
 * inactive pages and the carets read subtle. Renders nothing for a single page.
 */
export function PaginationNav({
  page,
  totalPages,
  onPageChange,
  className,
}: PaginationNavProps) {
  if (totalPages <= 1) return null;

  const pages = buildPageList(page, totalPages);

  return (
    <nav
      aria-label="Pagination"
      className={cn('flex items-center gap-2', className)}
    >
      <Button
        variant="ghost"
        size="icon"
        className="size-8 rounded-lg text-ds-text-subtle"
        onClick={() => onPageChange(Math.max(1, page - 1))}
        disabled={page <= 1}
        aria-label="Previous page"
      >
        <CaretLeft />
      </Button>

      <div className="flex items-center gap-0.5">
        {pages.map((p, i) =>
          p === '…' ? (
            <span
              key={`ellipsis-${i}`}
              aria-hidden="true"
              className="flex size-8 items-center justify-center ds-text-body-sm-medium text-ds-text-subtle"
            >
              …
            </span>
          ) : (
            <Button
              key={p}
              variant="ghost"
              size="icon"
              aria-label={`Page ${p}`}
              aria-current={p === page ? 'page' : undefined}
              className={cn(
                'size-8 rounded-lg ds-text-body-sm-medium tabular-nums',
                p === page
                  ? 'bg-ds-brand-subtlest text-ds-brand-subtle hover:bg-ds-brand-subtlest'
                  : 'text-ds-text-subtle'
              )}
              onClick={() => onPageChange(p)}
            >
              {p}
            </Button>
          )
        )}
      </div>

      <Button
        variant="ghost"
        size="icon"
        className="size-8 rounded-lg text-ds-text-subtle"
        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages}
        aria-label="Next page"
      >
        <CaretRight />
      </Button>
    </nav>
  );
}

/**
 * Smart page window (5-slot, matching Figma node 987:4399): keeps a contiguous
 * run of pages next to the current one, plus the last page, with an ellipsis
 * over the gap. Near the start it reads `1 2 3 … 11`, in the middle
 * `1 … 6 … 11`, and near the end `1 … 9 10 11` — so the numbers you'd actually
 * click are always visible, never a far-off `… 10 11` on page one.
 */
function buildPageList(page: number, totalPages: number): (number | '…')[] {
  if (totalPages <= 5)
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (page <= 3) return [1, 2, 3, '…', totalPages];
  if (page >= totalPages - 2)
    return [1, '…', totalPages - 2, totalPages - 1, totalPages];
  return [1, '…', page, '…', totalPages];
}
